import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import {
  createIssueTransaction,
  updateIssueTransaction,
  getIssueTransaction,
  getStudentsForIssue,
  fetchIssueOptions,
} from './api/issueItemApi'
import { appToast } from '../../../components/AppToast'
import AppDropdown from '../../../components/AppDropdown'
import AppDateInput from '../../../components/AppDateInput'
import AppToggle from '../../../components/AppToggle'
import AppFormFooterBar from '../../../components/AppFormFooterBar'

const DEPARTMENT_OPTIONS = [
  { label: 'All Departments', value: 'All' },
  { label: 'Academic', value: 'Academic' },
  { label: 'Administration', value: 'Administration' },
  { label: 'Accounts', value: 'Accounts' },
]

const PLACE_OPTIONS = [
  { label: 'Select Place...', value: '' },
  { label: 'Hall A', value: 'Hall A' },
  { label: 'Hall B', value: 'Hall B' },
  { label: 'Library', value: 'Library' },
]

const FEEAMOUNT_OPTIONS = [
  { label: 'FeeAmount', value: 'FeeAmount' },
  { label: '% Amount', value: '% Amount' },
]

export default function IssueItemEntryScreen() {
  const navigate = useNavigate()
  const { id: routeId } = useParams()
  const [searchParams] = useSearchParams()
  const editId = routeId || searchParams.get('id')
  const isEdit = Boolean(editId)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [students, setStudents] = useState([])
  const [selectedStudents, setSelectedStudents] = useState(new Set())
  const [studentsLoaded, setStudentsLoaded] = useState(false)

  const [options, setOptions] = useState({
    institutes: [],
    branches: [],
    departments: DEPARTMENT_OPTIONS,
    places: PLACE_OPTIONS,
    courses: [],
    batches: [],
    subjects: [],
    academicYears: [],
    items: [],
  })

  // Column 1 state
  const [institute, setInstitute] = useState('')
  const [branch, setBranch] = useState('')
  const [department, setDepartment] = useState('Academic')
  const [place, setPlace] = useState('')
  const [notReceived, setNotReceived] = useState(false)

  // Column 2 state
  const [aYear, setAYear] = useState('2026-27')
  const [course, setCourse] = useState('')
  const [batch, setBatch] = useState('')
  const [subject, setSubject] = useState('')
  const [feeAmountType, setFeeAmountType] = useState('FeeAmount')
  const [feeAmount, setFeeAmount] = useState('')

  // Column 3 state
  const [voucherNo, setVoucherNo] = useState('')
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split('T')[0])
  const [item, setItem] = useState('')
  const [quantity, setQuantity] = useState('1.00')

  // Remarks state
  const [remarks, setRemarks] = useState('')

  // Load options on mount
  useEffect(() => {
    fetchIssueOptions()
      .then((opts) => {
        if (opts) {
          setOptions({
            institutes: opts.institutes || [],
            branches: opts.branches || [],
            departments: opts.departments?.length ? opts.departments : DEPARTMENT_OPTIONS,
            places: opts.places?.length ? opts.places : PLACE_OPTIONS,
            courses: [{ label: 'All Courses', value: '' }, ...(opts.courses || [])],
            batches: [{ label: 'All Batches', value: '' }, ...(opts.batches || [])],
            subjects: [{ label: 'All Subjects', value: '' }, ...(opts.subjects || [])],
            academicYears: opts.academicYears || [{ label: '2026-27', value: '2026-27' }],
            items: opts.items || [],
          })
          if (!isEdit) {
            if (opts.institutes?.length) setInstitute(opts.institutes[0].value)
            if (opts.branches?.length) setBranch(opts.branches[0].value)
            if (opts.items?.length) setItem(opts.items[0].value)
          }
        }
      })
      .catch(() => {})
  }, [isEdit])

  // Load data for edit
  useEffect(() => {
    if (isEdit) {
      loadForEdit(editId)
    }
  }, [editId, isEdit])

  const loadForEdit = async (id) => {
    try {
      setIsLoading(true)
      const tx = await getIssueTransaction(id)
      setInstitute(tx.institute || '')
      setBranch(tx.branch || '')
      setDepartment(tx.department || 'Academic')
      setPlace(tx.place || '')
      setNotReceived(Boolean(tx.notReceived))
      setRemarks(tx.remarks || '')
      setAYear(tx.aYear || '2026-27')
      setCourse(tx.course || '')
      setBatch(tx.batch || '')
      setSubject(tx.subject || '')
      setFeeAmountType(tx.feeAmountType || 'FeeAmount')
      setFeeAmount(tx.feeAmount != null ? String(tx.feeAmount) : '')
      setVoucherNo(tx.voucherNo || '')
      setVoucherDate(tx.voucherDate || new Date().toISOString().split('T')[0])
      setItem(tx.item || '')
      setQuantity(tx.quantity != null ? String(tx.quantity) : '1.00')

      const assignedStudents = tx.students || []
      setStudents(assignedStudents)
      setSelectedStudents(new Set(tx.studentIds || []))
      setStudentsLoaded(true)
    } catch (err) {
      appToast.error(err.response?.data?.detail || err.message || 'Failed to load transaction')
    } finally {
      setIsLoading(false)
    }
  }

  const handleShowStudents = async () => {
    if (!item) {
      appToast.error('Select an Item first')
      return
    }

    try {
      setIsLoading(true)
      const data = await getStudentsForIssue({
        course: course || undefined,
        batch: batch || undefined,
        subject: subject || undefined,
        aYear: aYear || undefined,
        item,
        notReceived,
        feeAmountType,
        feeAmount: feeAmount ? Number(feeAmount) : 0.0,
        institute: institute || undefined,
        branch: branch || undefined,
      })
      setStudents(data || [])
      setStudentsLoaded(true)
      if (data?.length > 0 && selectedStudents.size === 0) {
        setSelectedStudents(new Set(data.map((s) => s.id)))
      }
    } catch (err) {
      appToast.error(err.response?.data?.detail || err.message || 'Failed to load students')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleStudentSelection = (id) => {
    const newSelected = new Set(selectedStudents)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedStudents(newSelected)
  }

  const allSelected =
    students.length > 0 && students.every((student) => selectedStudents.has(student.id))

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedStudents(new Set(students.map((student) => student.id)))
    } else {
      setSelectedStudents(new Set())
    }
  }

  const handleSave = async () => {
    if (!item) {
      appToast.error('Select an Item first')
      return
    }

    if (!quantity || Number(quantity) <= 0) {
      appToast.error('Enter a valid quantity')
      return
    }

    if (selectedStudents.size === 0) {
      appToast.error('Select at least one student')
      return
    }

    const payload = {
      institute,
      branch,
      department,
      place,
      notReceived,
      remarks,
      aYear,
      course,
      batch,
      subject,
      feeAmountType,
      feeAmount: feeAmount ? Number(feeAmount) : 0.0,
      voucherNo: voucherNo || undefined,
      voucherDate,
      item,
      quantity: Number(quantity) || 1.0,
      selectedStudents: Array.from(selectedStudents),
    }

    try {
      setIsSaving(true)
      if (isEdit) {
        await updateIssueTransaction(editId, payload)
        appToast.success('Issue transaction updated successfully')
      } else {
        await createIssueTransaction(payload)
        appToast.success('Issue transaction created successfully')
      }
      navigate('/admission/issue-item/list')
    } catch (err) {
      appToast.error(err.response?.data?.detail || err.message || 'Failed to save transaction')
    } finally {
      setIsSaving(false)
    }
  }

  const itemInfo = () => {
    if (!item) {
      appToast.info('Select an item to view specifications')
      return
    }
    appToast.info(`Item: ${item} — Standard Courseware Material`)
  }

  const itemAdd = () => {
    navigate('/masters/inventory/entry')
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Header Title & Navigation ──────────────────────────────────────── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Admission Module
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Issue Item' : 'Issue Inventory Item to Students'}
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant font-medium">
            Specify issuance parameters, voucher metadata, item details, and student assignments.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/admission/issue-item/list"
            className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer shadow-xs"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back to List</span>
          </Link>
        </div>
      </section>

      {/* ── Main Form Card ─────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-sm">
        <div className="border-b border-outline-variant/15 pb-4 mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-on-surface">
              Issue Parameters &amp; Voucher Details
            </h2>
            <p className="text-xs text-on-surface-variant">
              All mandatory fields marked with an asterisk (<span className="text-error font-bold">*</span>)
            </p>
          </div>
          {isEdit && (
            <span className="rounded-lg bg-primary/10 px-3 py-1 font-mono text-xs font-bold text-primary">
              Editing #{editId}
            </span>
          )}
        </div>

        {/* 3-Column Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 items-start">
          {/* ── COLUMN 1: Scope & Placement ── */}
          <div className="flex flex-col gap-4">
            {/* Institute */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Institute<span className="text-error"> *</span>
              </label>
              <AppDropdown
                options={options.institutes}
                value={institute}
                onChange={setInstitute}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Branch */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Branch<span className="text-error"> *</span>
              </label>
              <AppDropdown
                options={options.branches}
                value={branch}
                onChange={setBranch}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Department */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Department<span className="text-error"> *</span>
              </label>
              <AppDropdown
                options={options.departments}
                value={department}
                onChange={setDepartment}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Place */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Place
              </label>
              <AppDropdown
                options={options.places}
                value={place}
                onChange={setPlace}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Not received toggle */}
            <div className="flex items-center gap-3 pt-2">
              <AppToggle
                checked={notReceived}
                onChange={setNotReceived}
                ariaLabel="who have Not received item"
              />
              <span className="text-xs font-semibold text-on-surface select-none">
                who have Not received item
              </span>
            </div>
          </div>

          {/* ── COLUMN 2: Academic & Fees Filters ── */}
          <div className="flex flex-col gap-4">
            {/* A.Year */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                A.Year
              </label>
              <AppDropdown
                options={options.academicYears}
                value={aYear}
                onChange={setAYear}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Course */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Course
              </label>
              <AppDropdown
                options={options.courses}
                value={course}
                onChange={setCourse}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Batch */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Batch
              </label>
              <AppDropdown
                options={options.batches}
                value={batch}
                onChange={setBatch}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Subject */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Subject
              </label>
              <AppDropdown
                options={options.subjects}
                value={subject}
                onChange={setSubject}
                triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
              />
            </div>

            {/* Feeamount filter */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Fee Eligibility Threshold
              </label>
              <div className="flex items-center gap-2">
                <div className="w-1/2">
                  <AppDropdown
                    options={FEEAMOUNT_OPTIONS}
                    value={feeAmountType}
                    onChange={setFeeAmountType}
                    triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                  placeholder="Min amount..."
                  className="h-10 w-1/2 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
                />
              </div>
            </div>
          </div>

          {/* ── COLUMN 3: Item & Issuance Execution ── */}
          <div className="flex flex-col gap-4">
            {/* Voucher No. */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Voucher No.
              </label>
              <input
                type="text"
                readOnly
                placeholder="Auto-generated"
                value={voucherNo}
                className="h-10 w-full rounded-xl border border-outline-variant/20 bg-surface-container-low px-3 font-mono text-xs font-bold text-primary outline-none cursor-default"
              />
            </div>

            {/* Voucher Date */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Voucher Date
              </label>
              <AppDateInput
                value={voucherDate}
                onChange={setVoucherDate}
                placeholder="yyyy-mm-dd"
                inputClassName="h-10 text-xs font-semibold"
              />
            </div>

            {/* Item Selector */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Item<span className="text-error"> *</span>
              </label>
              <div className="flex items-center gap-1.5">
                <div className="flex-1 min-w-0">
                  <AppDropdown
                    options={options.items}
                    value={item}
                    onChange={setItem}
                    placeholder="Select Item..."
                    triggerClassName="h-10 bg-surface-container-lowest border border-outline-variant/30 text-xs font-semibold w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={itemInfo}
                  title="Item specifications"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base">info</span>
                </button>
                <button
                  type="button"
                  onClick={itemAdd}
                  title="Add new Item in Masters"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition cursor-pointer"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                </button>
              </div>
            </div>

            {/* Quantity */}
            <div className="grid gap-1.5">
              <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Quantity per Student<span className="text-error"> *</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="1.00"
                className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
            </div>

            {/* Show Students Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleShowStudents}
                disabled={isLoading || !item}
                className="h-10 w-full rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-xs flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-base">groups</span>
                <span>{isLoading ? 'Loading Students...' : 'Show Students'}</span>
              </button>
            </div>
          </div>

          {/* ── ROW: Full Width Remarks ── */}
          <div className="col-span-1 md:col-span-3 grid gap-1.5 pt-2">
            <label className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
              Remarks
            </label>
            <textarea
              rows={2}
              placeholder="Remarks and notes..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 text-xs font-medium text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/20 transition resize-none"
            />
          </div>
        </div>
      </section>

      {/* ── Student Selection & Assignments Layout ─────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 items-start">
        {/* Student Table (3 cols) */}
        <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest overflow-hidden shadow-sm lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3.5">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base text-primary">school</span>
                <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
                  Student Selection
                </span>
              </div>
              <button
                type="button"
                onClick={() => toggleSelectAll(!allSelected)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer shadow-xs"
              >
                <span className="material-symbols-outlined text-sm">
                  {allSelected ? 'check_box' : 'check_box_outline_blank'}
                </span>
                <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
              </button>
            </div>
            <div className="text-xs font-semibold text-on-surface-variant">
              Students Selected:{' '}
              <span className="font-bold text-primary font-mono">{selectedStudents.size}</span>
            </div>
          </div>

          {!studentsLoaded ? (
            <div className="p-8 text-center">
              <span className="material-symbols-outlined mb-2 block text-4xl text-primary/40">
                group_add
              </span>
              <p className="text-xs font-semibold text-on-surface">
                No entries meet selected criteria.
              </p>
              <p className="mt-1 text-xs text-on-surface-variant">
                Select an Item and click <strong className="text-primary font-bold">Show Students</strong> to populate eligible students.
              </p>
            </div>
          ) : students.length === 0 ? (
            <div className="p-8 text-center">
              <span className="material-symbols-outlined mb-2 block text-4xl text-outline/40">
                search_off
              </span>
              <p className="text-xs font-semibold text-on-surface">
                No eligible students found matching the selected course, batch, or fee criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto app-custom-scrollbar">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 z-10 bg-surface-container-low border-b border-outline-variant/20 text-on-surface-variant uppercase tracking-wider select-none font-bold">
                  <tr>
                    <th className="px-4 py-3 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-outline-variant/40 text-primary accent-primary cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3">Roll No</th>
                    <th className="px-4 py-3">ID Card</th>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-4 py-3">Course</th>
                    <th className="px-4 py-3">Batch</th>
                    <th className="px-4 py-3 text-right">Paid %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/15">
                  {students.map((student) => {
                    const isSelected = selectedStudents.has(student.id)
                    return (
                      <tr
                        key={student.id}
                        onClick={() => toggleStudentSelection(student.id)}
                        className={`transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-primary/5 hover:bg-primary/10'
                            : 'hover:bg-surface-container-low/40'
                        }`}
                      >
                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="h-4 w-4 rounded border-outline-variant/40 text-primary accent-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-on-surface">{student.rollNo}</td>
                        <td className="px-4 py-2.5 font-mono font-bold text-primary">{student.idCard}</td>
                        <td className="px-4 py-2.5 font-semibold text-on-surface">{student.name}</td>
                        <td className="px-4 py-2.5 text-on-surface-variant">{student.course}</td>
                        <td className="px-4 py-2.5 text-on-surface-variant">{student.batch}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-on-surface">
                          {student.paidPercent ? `${student.paidPercent.toFixed(0)}%` : '0%'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Issuance Summary & Instructions (1 col) */}
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-5 shadow-sm">
            <h3 className="font-display text-sm font-bold text-on-surface flex items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-primary text-lg">info</span>
              <span>Distribution Rules</span>
            </h3>
            <ul className="space-y-2 text-xs text-on-surface-variant">
              <li className="flex items-start gap-1.5">
                <span className="text-primary font-bold">•</span>
                <span>Toggle <strong>who have Not received item</strong> to automatically filter out students with existing distribution records.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-primary font-bold">•</span>
                <span>Filter by <strong>Fee Eligibility Threshold</strong> (e.g. min 50% paid) to enforce fee recovery before kit dispatch.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-primary font-bold">•</span>
                <span>Saving automatically synchronizes records to each student&apos;s personal <strong>Inventory Profile</strong>.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Fixed Form Footer Bar ─────────────────────────────────────────── */}
      <AppFormFooterBar
        cancelPath="/admission/issue-item/list"
        isSubmitting={isSaving}
        submitLabel={isEdit ? 'Update Issue' : 'Issue Item to Selected'}
        submittingLabel={isEdit ? 'Updating...' : 'Issuing Item...'}
        onSubmit={handleSave}
      />
    </div>
  )
}
